import React, { createContext, useContext, useState, useEffect } from 'react';
import { useProperties } from '../hooks/useProperties';
import { calculateDataRanges } from '../utils/calculateDataRanges';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within SearchProvider');
  }
  return context;
};

const getSubdomain = () => {
  if (process.env.REACT_APP_INVESTMENT_SLUG) {
    return process.env.REACT_APP_INVESTMENT_SLUG;
  }

  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  // Handle localhost development (e.g., test.localhost)
  if (hostname.includes('localhost')) {
    if (parts.length > 1 && parts[0] !== 'localhost') {
      return parts[0];
    }
    // Default subdomain for debugging
    return 'warszawska-33';
  }
  
  // Handle regular domains (e.g., tenant.yourdomain.com)
  if (parts.length > 2) {
    const subdomain = parts[0];
    return subdomain === 'www' ? null : subdomain;
  }
  
  // Default subdomain for debugging when no subdomain is found
  return 'warszawska-33';
};

export const SearchProvider = ({ children }) => {
  // Get subdomain immediately to use as initial state
  const subdomain = getSubdomain();
  const [tenant, setTenant] = useState(subdomain);
  const [investmentSlug, setInvestmentSlug] = useState(subdomain);
  
  const { properties, optionalUnits, allPropertiesForMap, investmentData, footerConfig, globalConfig, isMultiInvestment, loading, error } = useProperties(investmentSlug);
  
  // Calculate available property types and features from properties
  const getAvailableOptions = () => {
    if (!properties || properties.length === 0) {
      return { propertyTypes: [], features: [] };
    }
    
    const propertyTypesMap = new Map();
    properties.forEach(p => {
      if (p.propertyType) {
        propertyTypesMap.set(p.propertyType, p.propertyTypeLabel);
      }
    });
    
    const propertyTypes = Array.from(propertyTypesMap.entries()).map(([key, label]) => ({ key, label }));
    
    // Collect features from features.all array (unified approach)
    const featuresMap = new Map();
    properties.forEach(property => {
      const allFeatures = property.specifications?.features?.all || [];
      allFeatures.forEach(f => {
        if (f.key && f.label) {
          featuresMap.set(f.key, { label: f.label, icon_svg: f.icon_svg });
        }
      });
    });

    const features = Array.from(featuresMap.entries()).map(([key, data]) => ({
      key,
      label: data.label,
      icon_svg: data.icon_svg
    }));
    
    return {
      propertyTypes,
      features
    };
  };
  
  const { propertyTypes: availablePropertyTypes, features: availableFeatures } = getAvailableOptions();
  
  

  // Calculate initial filter ranges based on loaded properties
  const getInitialFilters = () => {
    if (properties && properties.length > 0) {
      const ranges = calculateDataRanges(properties);
      
      // Create propertyTypes filter based on available types
      const propertyTypesFilter = {};
      availablePropertyTypes.forEach(type => {
        propertyTypesFilter[type.key] = true;
      });
      
      // Create features filter based on available features
      const featuresFilter = {};
      availableFeatures.forEach(feature => {
        featuresFilter[feature.key] = false;
      });
      
      return {
        onlyAvailable: true,
        onlyApartments: false,
        propertyTypes: propertyTypesFilter,
        floor: [ranges.floor.min, ranges.floor.max],
        rooms: [ranges.rooms.min, ranges.rooms.max],
        price: [ranges.price.min, ranges.price.max],
        area: [ranges.area.min, ranges.area.max],
        features: featuresFilter
      };
    }
  };

  const [filters, setFilters] = useState({
    onlyAvailable: true,
    onlyApartments: false,
    propertyTypes: {},
    floor: [0, 0],
    rooms: [0, 0],
    price: [0, 0],
    area: [0, 0],
    features: {}
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Update filters when properties are loaded
  useEffect(() => {
    if (properties && properties.length > 0 && !loading) {
      const initialFilters = getInitialFilters();
      if (initialFilters) {
        setFilters(initialFilters);
      }
    }
  }, [properties, loading]);


  const handleSwitchChange = (field) => {
    setFilters(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleDualRangeChange = (field, values) => {
    setFilters(prev => ({
      ...prev,
      [field]: values
    }));
  };

  const handleFeatureChange = (feature) => {
    setFilters(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const handlePropertyTypeChange = (propertyType) => {
    setFilters(prev => ({
      ...prev,
      propertyTypes: {
        ...prev.propertyTypes,
        [propertyType]: !prev.propertyTypes[propertyType]
      }
    }));
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const resetFilters = () => {
    const initialFilters = getInitialFilters();
    if (initialFilters) {
      setFilters(initialFilters);
    }
  };

  const filteredProperties = (properties || []).filter(property => {
    // Debug logging
    // console.log('Filtering property:', property.title, 'Floor:', property.floor?.levelNumber, 'Rooms:', property.specifications?.rooms?.total);
    // console.log('Filter ranges - Floor:', filters.floor, 'Rooms:', filters.rooms);
    
    if (filters.onlyAvailable && property.status.code !== 'available') {
      return false;
    }

    if (filters.onlyApartments && property.propertyType !== 'lokal-mieszkalny') {
      return false;
    }

    // Property type filter
    const enabledPropertyTypes = Object.entries(filters.propertyTypes || {})
      .filter(([_, enabled]) => enabled)
      .map(([type, _]) => type);
    
    if (enabledPropertyTypes.length > 0) {
      const propertyTypeMatch = enabledPropertyTypes.some(type => {
        switch (type) {
          case 'lokal-mieszkalny':
            return property.propertyType === 'lokal-mieszkalny' || !property.propertyType;
          case 'garaz':
            return property.propertyType === 'garaz';
          case 'komorka':
            return property.propertyType === 'komorka';
          case 'lokal uslugowy':
            return property.propertyType === 'lokal uslugowy';
          case 'house':
            return property.propertyType === 'house';
          default:
            return false;
        }
      });
      if (!propertyTypeMatch) return false;
    }

    const [minFloor, maxFloor] = filters.floor || [0, 0];
    const floorNumber = property.floor?.levelNumber ?? property.location?.floor;
    if (floorNumber != null && (floorNumber < minFloor || floorNumber > maxFloor)) {
      return false;
    }

    const [minRooms, maxRooms] = filters.rooms || [0, 0];
    const roomsTotal = property.specifications?.rooms?.total;
    const roomsExtraVariant = property.specifications?.rooms?.totalExtraVariant;
    
    // Check if property matches room filter through either rooms_total or rooms_total_extra_variant
    if (roomsTotal != null) {
      const matchesMainRooms = roomsTotal >= minRooms && roomsTotal <= maxRooms;
      const matchesVariantRooms = roomsExtraVariant != null && roomsExtraVariant >= minRooms && roomsExtraVariant <= maxRooms;
      
      if (!matchesMainRooms && !matchesVariantRooms) {
        return false;
      }
    }

    const [minPrice, maxPrice] = filters.price || [0, 0];
    if (property.pricing.totalPrice.value < minPrice || property.pricing.totalPrice.value > maxPrice) {
      return false;
    }

    const [minArea, maxArea] = filters.area || [0, 0];
    if (property.specifications.area.total < minArea || property.specifications.area.total > maxArea) {
      return false;
    }

    // Feature filtering using unified features.all array
    for (const [featureKey, required] of Object.entries(filters.features || {})) {
      if (required) {
        const allFeatures = property.specifications?.features?.all || [];
        const hasFeature = allFeatures.some(f => f.key === featureKey);
        if (!hasFeature) return false;
      }
    }

    return true;
  });

  const value = {
    properties,
    optionalUnits,
    allPropertiesForMap,
    filteredProperties,
    investmentData,
    footerConfig,
    globalConfig,
    isMultiInvestment,
    loading,
    error,
    filters,
    isExpanded,
    tenant,
    // Available options from properties
    availablePropertyTypes,
    availableFeatures,
    // Filter controls configuration
    hideFilterControls: investmentData?.hideFilterControls || false,
    handleSwitchChange,
    handleDualRangeChange,
    handleFeatureChange,
    handlePropertyTypeChange,
    toggleExpanded,
    resetFilters
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};